import type { ClubIdentity } from '../lib/equipment.ts';
import { clubDisplayLabel, clubQualifier } from '../lib/equipment.ts';

/* Le nom d'un club, hiérarchisé.
 *
 * ── Ce qui a changé, et pourquoi ────────────────────────────────────────────
 *
 * Le libellé était plat : « Fer 7 · Inesis 33° » tout au même niveau. L'angle
 * réel se lisait donc aussi fort que le type, alors qu'il n'est presque jamais
 * ce qu'on cherche. Sur le tapis, on cherche « le fer 7 » — pas 33 degrés, pas
 * Inesis. Le nombre attirait l'œil et retardait la reconnaissance à chaque
 * lecture, ce qui coûte cher sur un écran qu'on consulte entre deux balles.
 *
 * Le TYPE porte donc l'identité ; la marque, l'angle et le surnom passent en
 * retrait. Ils ne disparaissent pas : deux fers 7 de marques différentes sont
 * deux clubs distincts, avec deux séries de statistiques, et il faut pouvoir
 * les départager.
 *
 * ── La discrétion se fait par la taille, pas par le contraste ───────────────
 *
 * On lit cet écran en plein soleil. Effacer le qualificatif en baissant son
 * opacité l'aurait rendu illisible dehors, alors qu'il reste une information
 * réelle. La hiérarchie passe donc par le corps et la graisse — 16 px
 * semi-gras contre 12 px normal, deux crans immédiatement perçus — et non par
 * le contraste.
 *
 * L'opacité retenue est mesurée, pas choisie : à 0,70 le qualificatif tombait
 * à 3,75:1 sous un voile solaire de 8 %, sous l'AA de 4,5:1 — et à 12 px,
 * aucune tolérance « grand texte » ne s'applique. Un balayage donne 0,85 comme
 * plus faible valeur qui tienne (4,84:1 sous voile).
 *
 * ── Mon matériel n'utilise pas ce composant ─────────────────────────────────
 *
 * Là-bas, on vient précisément gérer des clubs : la marque et l'angle sont le
 * sujet, pas un détail. Cet écran garde `clubDisplayLabel` et son libellé
 * complet sur une ligne.
 */

interface Props {
    club: ClubIdentity;
    /** `inline` : le qualificatif suit sur la même ligne, pour les listes
     *  serrées. `stacked` : il passe dessous, quand la largeur manque. */
    layout?: 'inline' | 'stacked';
    /** Classes du type, pour l'accorder au bloc qui l'accueille. */
    className?: string;
}

export function ClubLabel({ club, layout = 'inline', className = '' }: Props) {
    const qualifier = clubQualifier(club);

    // Un nom accessible complet : un lecteur d'écran ne perçoit ni corps ni
    // graisse, et « Fer 7 » seul ne distinguerait pas les deux fers 7.
    const full = clubDisplayLabel(club);

    if (layout === 'stacked') {
        return (
            <span className="block min-w-0" title={full}>
                <span className={`block truncate font-semibold ${className}`}>{club.label}</span>
                {qualifier && (
                    <span className="block truncate text-xs font-normal opacity-85">{qualifier}</span>
                )}
            </span>
        );
    }

    return (
        <span className="inline min-w-0" title={full}>
            <span className={`font-semibold ${className}`}>{club.label}</span>
            {qualifier && (
                <span className="ml-1.5 text-xs font-normal opacity-85">{qualifier}</span>
            )}
        </span>
    );
}
